/**
 * useTaskChecklistTemplates - Hook para gestão e aplicação de Templates de Checklist de Tarefas
 *
 * Cobre:
 *  - Listagem (list_task_checklist_templates) respeitando visibilidade (private / shared / by_profile / public)
 *  - Detalhes com grupos + itens (get_task_checklist_template)
 *  - Salvar (create/update) template completo em uma transação (save_task_checklist_template)
 *  - Excluir template (delete_task_checklist_template)
 *  - Aplicar template a uma tarefa nos modos "append" ou "replace" (apply_task_checklist_template)
 *
 * A gestão de visibilidade em si é feita por `ManageVisibilityDialog`
 * (entityType = 'task_checklist_template') via as RPCs unificadas
 * `get_entity_visibility` / `set_entity_visibility`.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Versão inicial da Fase 3.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTaskChecklistTemplates');

/** Item resumido em listagem */
export interface TaskChecklistTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  visibility: 'private' | 'shared' | 'by_profile' | 'public';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  group_count: number;
  item_count: number;
}

/** Estrutura completa (para edição / preview) */
export interface TaskChecklistTemplateDetail {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  visibility: TaskChecklistTemplateSummary['visibility'];
  created_at: string;
  groups: Array<{
    id: string;
    title: string;
    sort_order: number;
    items: Array<{ id: string; content: string; sort_order: number }>;
  }>;
}

/** Payload aceito por save_task_checklist_template */
export interface SaveTaskChecklistTemplateInput {
  templateId?: string | null;
  name: string;
  description?: string | null;
  isActive?: boolean;
  groups: Array<{ title: string; items: Array<{ content: string }> }>;
}

/**
 * Lista templates visíveis para o usuário no tenant atual.
 * @param onlyActive quando `true` retorna apenas ativos; `undefined` retorna todos.
 */
export function useTaskChecklistTemplatesList(onlyActive?: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['task-checklist-templates', tenantId, onlyActive ?? 'all'],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<TaskChecklistTemplateSummary[]> => {
      const { data, error } = await (supabase.rpc as any)('list_task_checklist_templates', {
        p_only_active: onlyActive ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Falha ao listar templates', { error: error.message });
        throw error;
      }
      return (data as TaskChecklistTemplateSummary[]) ?? [];
    },
  });
}

/**
 * Carrega um template completo com grupos e itens.
 */
export function useTaskChecklistTemplateDetail(templateId: string | null | undefined, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['task-checklist-template', templateId, tenantId],
    enabled: enabled && !!templateId && !!tenantId,
    staleTime: 15_000,
    queryFn: async (): Promise<TaskChecklistTemplateDetail> => {
      const { data, error } = await (supabase.rpc as any)('get_task_checklist_template', {
        p_template_id: templateId,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Falha ao carregar template', { templateId, error: error.message });
        throw error;
      }
      return data as TaskChecklistTemplateDetail;
    },
  });
}

/**
 * Cria ou atualiza template completo (grupos + itens) em uma transação.
 * Retorna o `id` do template.
 */
export function useSaveTaskChecklistTemplate() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveTaskChecklistTemplateInput): Promise<string> => {
      const { data, error } = await (supabase.rpc as any)('save_task_checklist_template', {
        p_template_id: input.templateId ?? null,
        p_name: input.name,
        p_description: input.description ?? null,
        p_is_active: input.isActive ?? true,
        p_groups: input.groups,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Falha ao salvar template', { error: error.message });
        throw error;
      }
      return data as string;
    },
    onSuccess: (_id, input) => {
      queryClient.invalidateQueries({ queryKey: ['task-checklist-templates'] });
      if (input.templateId) {
        queryClient.invalidateQueries({ queryKey: ['task-checklist-template', input.templateId] });
      }
      showToast.success(input.templateId ? 'Template atualizado' : 'Template criado');
    },
    onError: (err: Error) => {
      showToast.error('Erro ao salvar template', err.message);
    },
  });
}

/**
 * Exclui template (apenas admin do tenant).
 */
export function useDeleteTaskChecklistTemplate() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await (supabase.rpc as any)('delete_task_checklist_template', {
        p_template_id: templateId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Falha ao excluir template', { templateId, error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-checklist-templates'] });
      showToast.success('Template excluído');
    },
    onError: (err: Error) => {
      showToast.error('Erro ao excluir template', err.message);
    },
  });
}

/**
 * Aplica um template à tarefa informada.
 * `mode='append'` soma ao checklist atual; `mode='replace'` apaga antes.
 */
export function useApplyTaskChecklistTemplate() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { taskId: string; templateId: string; mode: 'append' | 'replace' }) => {
      const { data, error } = await (supabase.rpc as any)('apply_task_checklist_template', {
        p_task_id: input.taskId,
        p_template_id: input.templateId,
        p_mode: input.mode,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Falha ao aplicar template', { ...input, error: error.message });
        throw error;
      }
      return data as number;
    },
    onSuccess: (groupsCreated, input) => {
      // Invalida checklist da tarefa + timeline (log em task_activity_log)
      queryClient.invalidateQueries({ queryKey: ['task-checklist', input.taskId] });
      queryClient.invalidateQueries({ queryKey: ['task-activity-log', input.taskId] });
      showToast.success(
        groupsCreated > 0
          ? `Template aplicado (${groupsCreated} ${groupsCreated === 1 ? 'lista adicionada' : 'listas adicionadas'})`
          : 'Template aplicado',
      );
    },
    onError: (err: Error) => {
      showToast.error('Erro ao aplicar template', err.message);
    },
  });
}
